import React, { Component } from 'react';
import { connect } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { StockItemArtistInterface } from '../../app/Search/containers/MenuContainerInterfaces';
import { ContentClass } from '../../app/Shared/enums';
import { contentTypes } from '../../common/Constants';
import { SiteEnum } from '../../common/SiteConstants/SiteConstants';
import Duration from '../../common/components/Duration';
import { StockItem, StockItemFormat } from '../../common/types/StockItemTypes';
import { getArtistFullName, getCurrentSite } from '../../common/utils';
import {
  selectDetailsAcquisitionOption,
  selectDetailsStockItem,
  selectDetailsStockItemArtists,
  selectDetailsStockItemIsContentClass,
} from '../selectors/detailsSelectors';
import { getLabel } from './AssetId';
import ColorPalette from './ColorPalette';

import './StockItemInfo.less';

interface Props {
  stockItem: StockItem;
  stockItemFormat: StockItemFormat;
  stockItemArtists: StockItemArtistInterface[];
  isVideoDetailsPage?: boolean;
  isImageDetailsPage?: boolean;
}

class StockSpecs extends Component<Props> {
  static labels = {
    dimensions: 'Resolution',
    dpi: 'DPI',
    colors: 'Colors',
    hasTalentReleased: 'Model Release',
    hasPropertyReleased: 'Property Release',
    isEditorial: 'Editorial',
    contributor: 'Contributor',
    rating: 'Rating',
    downloads: 'Downloads',
    duration: 'Duration',
    frameRate: 'Frame Rate',
    hasAlphaChannel: 'Alpha Channel',
    softwarePlugins: 'Required Plugins',
    softwareVersion: 'Software Version',
    resolution: 'Resolution',
    maxResolution: 'Resolution',
    bpm: 'BPM',
    type: 'Type',
    artists: 'Artists',
    artistFullNames: 'Artist Full Names',
    pro: 'Pro',
    publisher: 'Publisher',
    publisherPro: 'Publisher PRO',
    filename: 'Filename',
    assetId: 'Asset ID',
  };

  static artistKeys = [
    'artists',
    'artistFullNames',
    'publisher',
    'pro',
    'publisherPro',
  ];

  render() {
    const { isVideoDetailsPage, isImageDetailsPage } = this.props;
    const isDetailsPageRedesign = isVideoDetailsPage || isImageDetailsPage;
    const keys = this.getFilteredSpecKeys();
    const colors = this.renderColors();

    return (
      <>
        <ul
          className={twMerge(
            'stockItemInfo-stockSpec',
            isDetailsPageRedesign && 'text-white text-sm space-y-4 mt-4'
          )}
        >
          {keys.map((key) => this.renderItem(key))}
        </ul>
        {colors}
      </>
    );
  }

  renderLabel(key) {
    if (key === 'assetId') {
      return getLabel(this.props.stockItem.contentType);
    }
    return StockSpecs.labels[key] || key;
  }

  renderItem(key) {
    const { stockItemFormat, stockItem } = this.props;

    const formatValue = stockItemFormat[key];

    const itemValue = StockSpecs.artistKeys.includes(key)
      ? this.getArtistValue(key)
      : stockItem[key];

    const value = formatValue || itemValue;

    if (value === undefined) {
      return null;
    }

    return (
      <li key={key} className="stockItemInfo-stockSpecItem">
        <span className="stockItemInfo-stockSpecItemKey">
          {this.renderLabel(key)}
        </span>
        :&nbsp;
        <span className="stockItemInfo-stockSpecItemValue">
          {this.renderSpec(key, value)}
        </span>
      </li>
    );
  }

  getSpecKeys() {
    const { stockItem } = this.props;

    const site = getCurrentSite();
    const hasContributorInfo = stockItem.contributor;
    const isAfterEffectsTemplate =
      stockItem.searchType === contentTypes.TEMPLATE;
    const hasMaxResolution = stockItem.maxResolution;

    if (site === SiteEnum.Graphicstock) {
      const keys = [
        'resolution',
        'dpi',
        'colors',
        'hasTalentReleased',
        'hasPropertyReleased',
        'isEditorial',
        'downloads',
        'rating',
        'assetId',
      ];

      if (hasContributorInfo) {
        keys.push('contributor');
      }

      return keys;
    }

    if (site === SiteEnum.Videoblocks) {
      let keys = [
        'duration',
        'frameRate',
        'softwarePlugins',
        'softwareVersion',
      ];

      if (!isAfterEffectsTemplate) {
        keys.push('hasAlphaChannel');
      }

      keys = [
        keys,
        'hasTalentReleased',
        'hasPropertyReleased',
        'isEditorial',
      ].flat();

      if (hasMaxResolution) {
        keys = ['maxResolution', keys].flat();
      } else {
        keys = ['resolution', keys].flat();
      }

      if (hasContributorInfo) {
        keys = ['contributor', keys, 'rating'].flat();
      } else {
        keys = [keys, 'downloads', 'rating'].flat();
      }

      return keys;
    }

    if (site === SiteEnum.Audioblocks) {
      let keys = ['filename', 'type', 'bpm', 'duration'];

      if (stockItem.searchType === 'music') {
        keys.push('artists');
      }

      keys = Array.from(
        new Set([keys, ...StockSpecs.artistKeys, 'rating'].flat())
      );

      return keys;
    }
  }

  getFilteredSpecKeys() {
    const excludedKeys = [
      'rating',
      'downloads',
      'filename',
      'colors',
      'contributor',
      'resolution',
      'maxResolution',
      'dimensions',
    ];

    return this.getSpecKeys().filter((key) => !excludedKeys.includes(key));
  }

  renderColors() {
    const { stockItem, isImageDetailsPage } = this.props;
    const colors = this.getSpecKeys().find((key) => key === 'colors');

    if (!colors) {
      return null;
    }

    return (
      <div
        className={twMerge(
          'stockItemInfo-stockSpecColors',
          isImageDetailsPage && 'text-white text-sm mt-4'
        )}
      >
        <span className="stockItemInfo-stockSpecItemKey">
          {this.renderLabel('colors')}
        </span>
        :&nbsp;
        <span className="stockItemInfo-stockSpecItemValue">
          {this.renderSpec('colors', stockItem.colors)}
        </span>
      </div>
    );
  }

  getArtistValue(key) {
    const { stockItemArtists } = this.props;

    switch (key) {
      case 'artists':
        const aliases = stockItemArtists
          .map((artist) => artist.alias)
          .filter(Boolean)
          .join(', ');
        return aliases.length ? aliases : undefined;
      case 'artistFullNames':
        return stockItemArtists
          .map(getArtistFullName)
          .filter(Boolean)
          .join(', ');
      case 'publisher':
        return stockItemArtists
          .map((artist) => artist.publisher)
          .filter(Boolean)
          .join(', ');
      case 'pro':
        return stockItemArtists
          .map((artist) => artist.artistPro)
          .filter(Boolean)
          .join(', ');
      case 'publisherPro':
        return stockItemArtists
          .map((artist) => artist.publisherPro)
          .filter(Boolean)
          .join(', ');
      default:
        return undefined;
    }
  }

  renderSpec(key, value) {
    switch (key) {
      case 'dimensions':
        return `${value[0]} x ${value[1]}`;

      // Boolean values
      case 'hasTalentReleased':
      case 'hasPropertyReleased':
      case 'hasAlphaChannel':
      case 'isEditorial':
        return value ? 'Yes' : 'No';

      // Number values that need to be formatted
      case 'downloads':
        return value.toLocaleString();

      case 'duration':
        if (value) {
          return <Duration seconds={value} />;
        } else {
          return 'Not Available';
        }

      case 'colors':
        return <ColorPalette colors={value} />;

      case 'frameRate':
        return value;

      case 'contributor':
        return (
          <a className="contributor-link" href={value.portfolioLink}>
            {value.username}
          </a>
        );

      case 'softwarePlugins':
        return value ? value : 'None';

      case 'softwareVersion':
        return value ? value : 'Not Available';

      case 'type':
        if (value === 'sfx') {
          return 'Sound Effect';
        } else {
          return value.charAt(0).toUpperCase() + value.slice(1);
        }

      case 'pro':
        return value ? value : 'None';

      case 'publisher':
        return value ? value : 'None';

      case 'publisherPro':
        return value ? value : 'None';

      default:
        return value;
    }
  }
}

export function mapStateToProps(state) {
  return {
    stockItem: selectDetailsStockItem(state),
    stockItemArtists: selectDetailsStockItemArtists(state),
    stockItemFormat: selectDetailsAcquisitionOption(state),
    isVideoDetailsPage: selectDetailsStockItemIsContentClass(
      ContentClass.Video
    )(state),
    isImageDetailsPage: selectDetailsStockItemIsContentClass(
      ContentClass.Image
    )(state),
  };
}

export default connect(mapStateToProps)(StockSpecs);
